import { getAuth, signInWithCustomToken } from "firebase/auth";
import {
  makeRedirectUri,
  ResponseType,
  useAuthRequest,
  useAutoDiscovery,
} from "expo-auth-session";
import React, { useContext, useState } from "react";
import { Button, Text } from "react-native-paper";
import { functionsUri } from "../config/firebase";
import { LanguageContext } from "@atgof-common/language";
import { View } from "react-native";
import { AlertBox } from "../components/AlertBox";
import ErrorDetail from "../components/ErrorDetail";

export default function MicrosoftSignin() {
  const { ph } = useContext(LanguageContext);
  const discovery = useAutoDiscovery(
    "https://login.microsoftonline.com/common/v2.0",
  );
  const [signinStatus, setSigninStatus] = useState<{
    error?: any;
    inProgress?: boolean;
  }>({});
  const error = signinStatus.error;
  let [errorCode, errorDetail] = error?.type
    ? [error.type, error.error]
    : error?.code
      ? [error.code, error]
      : [null, null];
  const cancelled = errorCode === "cancel" || errorCode === "dismiss";
  const shouldTryAgain =
    cancelled ||
    errorCode === "error" ||
    errorCode === "auth/invalid-credential";
  const errorPhrase = cancelled ? "login-cancelled" : null;
  const [alertTitlePhrase, alertMessagePhrase] =
    errorDetail && !errorPhrase
      ? [undefined, "login-failed-with-reason"]
      : [errorPhrase || "login-failed", undefined];

  const [_, response, promptAsync] = useAuthRequest(
    {
      clientId: "7aa1f791-e401-4314-991e-ffcc165928c8",
      scopes: ["openid", "profile", "email"],
      redirectUri: makeRedirectUri({
        scheme: "atgof",
      }),
      responseType: ResponseType.IdToken,
      usePKCE: false,
      extraParams: { nonce: "default" },
    },
    discovery,
  );
  React.useEffect(() => {
    if (!signinStatus.inProgress) return;
    if (response?.type === "success" && response.params.id_token) {
      const token = response.params.id_token;
      const xhr = new XMLHttpRequest();
      xhr.onload = () => {
        if (xhr.status == 200) {
          signInWithCustomToken(getAuth(), xhr.response).catch((error) =>
            setSigninStatus(error),
          );
        } else
          setSigninStatus({
            error: {
              type: "error",
              error: `Unable to retrieve token (status ${xhr.status})`,
            },
          });
      };
      xhr.onerror = () => setSigninStatus({ error: xhr.response });
      xhr.responseType = "text";
      xhr.open("POST", functionsUri + "/tokenForAzureSignin", true);
      const form = new FormData();
      form.append("token", token);
      xhr.send(form);
    } else if (response) setSigninStatus({ error: response });
  }, [signinStatus.inProgress, response]);

  function signIn() {
    setSigninStatus({ inProgress: true });
    promptAsync();
  }

  return (
    <View style={{ gap: 4, marginBottom: 8 }}>
      <Button
        mode="contained"
        loading={signinStatus.inProgress}
        onPress={signIn}
      >
        {
          ph(
            signinStatus.inProgress ? "logging-in" : "log-in-with-microsoft",
          ) as string
        }
      </Button>
      {error ? (
        <View style={{ gap: 2, marginBottom: 8 }}>
          {alertTitlePhrase ? (
            <Text variant="titleSmall">{ph(alertTitlePhrase) as string}</Text>
          ) : null}
          {alertMessagePhrase ? (
            <Text>{ph(alertMessagePhrase) as string}</Text>
          ) : null}
          {shouldTryAgain ? (
            <Text>
              {ph(cancelled ? "try-again" : "try-again-later") as string}
            </Text>
          ) : null}
          {errorDetail ? (
            <ErrorDetail>{errorDetail.toString()}</ErrorDetail>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
